// utils/markdown.js — минимальный markdown → HTML для страницы /docs.
//
// Зачем своё: единственный потребитель — внутренние .md из server/docs,
// тащить react-markdown + remark-gfm в бандл ради одной страницы незачем.
// Поддержано ровно то, что в этих файлах есть: заголовки, ``` код, таблицы,
// списки, цитаты, ---, абзацы и inline `код`, **жирный**, *курсив*, [ссылки](url).
//
// Вход доверенный (файлы репозитория), но HTML всё равно экранируется: док
// может содержать <script> в примере, и он должен остаться текстом.

const esc = s => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const RE_HEAD = /^(#{1,6})\s+(.*)$/;
const RE_HR = /^\s*(---+|\*\*\*+|___+)\s*$/;
const RE_LI = /^\s*([-*+]|\d+\.)\s+(.*)$/;
const RE_QUOTE = /^\s*>\s?(.*)$/;
const RE_FENCE = /^\s*```/;
// строка-разделитель таблицы: |---|:--:| — обязателен хотя бы один дефис
const RE_TSEP = /^\s*\|?[\s:|-]*-[\s:|-]*\|[\s:|-]*$/;

const cells = row => row.trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim());

/** Инлайн-разметка. `код` вынимается первым, чтобы * и [ ] внутри него не сработали. */
export function inlineMarkdown(text) {
    const code = [];
    let t = esc(text).replace(/`([^`]+)`/g, (_, c) => `\u0000${code.push(`<code>${c}</code>`) - 1}\u0000`);
    t = t
        // Якорь (#slug) — ссылка на соседний документ внутри /docs: остаётся в той же
        // вкладке, hashchange на странице сам переключит документ. Остальное — новой вкладкой.
        .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, label, href) => href.startsWith('#')
            ? `<a href="${href}">${label}</a>`
            : `<a href="${href}" target="_blank" rel="noreferrer">${label}</a>`)
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        // курсив: между звёздочками не бывает пробела по краям, иначе «a * b * c» станет курсивом
        .replace(/(^|[^*\w])\*(\S(?:[^*\n]*\S)?)\*/g, '$1<em>$2</em>');
    return t.replace(/\u0000(\d+)\u0000/g, (_, i) => code[i]);
}

export function renderMarkdown(md) {
    const lines = String(md || '').replace(/\r\n/g, '\n').split('\n');
    const out = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];

        if (RE_FENCE.test(line)) {                       // ``` блок кода
            const lang = line.trim().slice(3).trim();
            const body = [];
            i++;
            while (i < lines.length && !RE_FENCE.test(lines[i])) body.push(lines[i++]);
            i++;                                          // закрывающий ```
            out.push(`<pre class="md-code"${lang ? ` data-lang="${esc(lang)}"` : ''}><code>${esc(body.join('\n'))}</code></pre>`);
            continue;
        }

        if (RE_HEAD.test(line)) {
            const [, hashes, text] = line.match(RE_HEAD);
            const n = hashes.length;
            out.push(`<h${n}>${inlineMarkdown(text)}</h${n}>`);
            i++;
            continue;
        }

        if (RE_HR.test(line)) { out.push('<hr />'); i++; continue; }

        // Таблица: строка с | и следом строка-разделитель
        if (line.includes('|') && RE_TSEP.test(lines[i + 1] || '')) {
            const head = cells(line);
            i += 2;
            const body = [];
            while (i < lines.length && lines[i].includes('|') && lines[i].trim()) body.push(cells(lines[i++]));
            const th = head.map(c => `<th>${inlineMarkdown(c)}</th>`).join('');
            const tr = body.map(r =>
                `<tr>${head.map((h, k) => `<td data-label="${esc(h)}">${inlineMarkdown(r[k] || '')}</td>`).join('')}</tr>`
            ).join('');
            out.push(`<div class="md-table"><table><thead><tr>${th}</tr></thead><tbody>${tr}</tbody></table></div>`);
            continue;
        }

        if (RE_LI.test(line)) {                          // список (один уровень)
            const ordered = /^\s*\d+\./.test(line);
            const items = [];
            while (i < lines.length && RE_LI.test(lines[i])) items.push(lines[i++].match(RE_LI)[2]);
            const tag = ordered ? 'ol' : 'ul';
            out.push(`<${tag}>${items.map(t => `<li>${inlineMarkdown(t)}</li>`).join('')}</${tag}>`);
            continue;
        }

        if (RE_QUOTE.test(line)) {
            const body = [];
            while (i < lines.length && RE_QUOTE.test(lines[i])) body.push(lines[i++].match(RE_QUOTE)[1]);
            out.push(`<blockquote>${inlineMarkdown(body.join(' '))}</blockquote>`);
            continue;
        }

        if (!line.trim()) { i++; continue; }

        const para = [];                                  // абзац до пустой строки или нового блока
        while (i < lines.length && lines[i].trim() && !RE_HEAD.test(lines[i]) && !RE_HR.test(lines[i])
               && !RE_LI.test(lines[i]) && !RE_QUOTE.test(lines[i]) && !RE_FENCE.test(lines[i])
               && !(lines[i].includes('|') && RE_TSEP.test(lines[i + 1] || ''))) {
            para.push(lines[i++]);
        }
        out.push(`<p>${inlineMarkdown(para.join(' '))}</p>`);
    }

    return out.join('\n');
}
